// In-game clock + the bathing routine state machine.
//
// The routine the wall demands: 15 min DRY → 5 min cool → 15 min STEAM →
// 5 min cool → leave through reception. Time only moves meaningfully while
// you're doing the right thing in the right place, so the player is pushed
// to actually sit in the saunas (where the creepypastas live).

import type { RoomId } from "./world/layout.js";

export type Phase = "dry" | "cool1" | "steam" | "cool2" | "leave" | "done";

const DRY_MIN = 15;
const STEAM_MIN = 15;
const COOL_MIN = 5;
const START_GAME_MIN = 20 * 60; // 20:00

export class Routine {
  phase: Phase = "dry";
  gameMinutes = START_GAME_MIN;
  dry = 0;
  steam = 0;
  cool = 0;

  /** Advance time. dtReal = seconds since last frame. */
  update(dtReal: number, room: RoomId, seated: boolean) {
    const rate = this.rate(room, seated); // game-minutes per real second
    const dGame = rate * dtReal;
    this.gameMinutes += dGame;

    switch (this.phase) {
      case "dry":
        if (room === "dry_sauna" && seated) {
          this.dry = Math.min(DRY_MIN, this.dry + dGame);
          if (this.dry >= DRY_MIN) {
            this.phase = "cool1";
            this.cool = 0;
          }
        }
        break;
      case "cool1":
        if (room === "rest") {
          this.cool = Math.min(COOL_MIN, this.cool + dGame);
          if (this.cool >= COOL_MIN) this.phase = "steam";
        }
        break;
      case "steam":
        if (room === "steam_sauna" && seated) {
          this.steam = Math.min(STEAM_MIN, this.steam + dGame);
          if (this.steam >= STEAM_MIN) {
            this.phase = "cool2";
            this.cool = 0;
          }
        }
        break;
      case "cool2":
        if (room === "rest") {
          this.cool = Math.min(COOL_MIN, this.cool + dGame);
          if (this.cool >= COOL_MIN) this.phase = "leave";
        }
        break;
    }
  }

  /** Game minutes per real second, by what the player is doing. */
  private rate(room: RoomId, seated: boolean): number {
    const inDry = room === "dry_sauna" && seated && this.phase === "dry";
    const inSteam = room === "steam_sauna" && seated && this.phase === "steam";
    if (inDry || inSteam) return 0.5; // 15 game-min in ~30 real-sec of sitting
    const cooling = room === "rest" && (this.phase === "cool1" || this.phase === "cool2");
    if (cooling) return 0.33; // 5 game-min in ~15 real-sec
    return 0.08; // ambient drift while wandering
  }

  canLeave(): boolean {
    return this.phase === "leave";
  }
  markDone() {
    this.phase = "done";
  }

  /** "HH:MM" for the wristwatch. */
  clockString(): string {
    const total = Math.floor(this.gameMinutes) % (24 * 60);
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }

  /** Tiny progress tag for the watch face label. */
  watchLabel(): string {
    switch (this.phase) {
      case "dry":
        return `DRY ${Math.floor(this.dry)}/15`;
      case "cool1":
        return `COOL ${Math.floor(this.cool)}/5`;
      case "steam":
        return `STEAM ${Math.floor(this.steam)}/15`;
      case "cool2":
        return `COOL ${Math.floor(this.cool)}/5`;
      case "leave":
        return "LEAVE NOW";
      default:
        return "—";
    }
  }

  /** The objective banner HTML. */
  objective(): string {
    const t = (s: string) => `<span class="timer">${s}</span>`;
    switch (this.phase) {
      case "dry":
        return `Sit in the DRY SAUNA &nbsp;${t(`${Math.floor(this.dry)} / 15 min`)}`;
      case "cool1":
        return `Cool down in the REST AREA &nbsp;${t(`${Math.floor(this.cool)} / 5 min`)}`;
      case "steam":
        return `Sit in the STEAM SAUNA &nbsp;${t(`${Math.floor(this.steam)} / 15 min`)}`;
      case "cool2":
        return `Cool down in the REST AREA &nbsp;${t(`${Math.floor(this.cool)} / 5 min`)}`;
      case "leave":
        return `Leave through ${t("RECEPTION")}`;
      default:
        return "";
    }
  }
}
